import { Row } from '@devexpress/dx-react-grid';
import { SLAPerformance } from 'api/resources/models/AutoGenerated';
import { convertMinToDayHourMin } from './helpers/convertMinToDayHourMin';
import { DataGridTypes } from 'devextreme-react/cjs/data-grid';


const formatAverageDuration = (value: string | number | Date) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return `Average Duration:\n${value}`;
  }

  return `Average Duration:\n${convertMinToDayHourMin(value, 'S')}`;
};

type DurationSummaryAccumulator = {
  totalMinutes: number;
  totalRows: number;
  timeType: string;
};

type ClaimsSummaryAccumulator = {
  totalRows: number;
  bookinAccuracyRows: number;
  onTime: number;
  late: number;
  early: number;
  notSet: number;
};

const isDurationSummaryRow = (value: unknown): value is {
  slaDuration: number | null;
  timeType?: string;
} => {
  return Boolean(value) && typeof value === 'object' && 'slaDuration' in (value as object);
};

const isClaimsSummaryRow = (value: unknown): value is {
  slaTitle?: string;
  eol?: string;
} => {
  return Boolean(value) && typeof value === 'object';
};


export const SLA_PERFORMANCE_REPORT ="SLA Performance V3 Report"
export const SLA_PERFORMANCE_INFO_MESSAGE ="Allows a detailed analysis of SLA Performance."

export const columns = [
  { name: 'repairerGroup', title: 'Repairer Group', groupIndex: 1 },
  { name: 'repairCode', title: 'Repair Code' },
  { name: 'regMark', title: 'Registration' },
  { name: 'repairerName', title: 'Repairer Name', groupIndex: 2 },
  { name: 'claimReference', title: 'Claim Reference' },
  { name: 'startDTG', title: 'SLA Start', type: 'date' },
  { name: 'expectedDataValue', title: 'SLA Expected End', type: 'date' },
  { name: 'actualDataValue', title: 'SLA Actual End', type: 'date' },
  {
    name: 'slaDuration',
    title: 'Duration',
    getCellValue: (row: Row) => {
      return convertMinToDayHourMin(row.slaDuration, row.timeType);
    },
  },
    {
    name: 'slaDurationConverted',   // TODO: we use converted values to display on the table, we still keep data from backend to handle AVG grouping correctly 
    title: 'Duration NEW',
  },

  { name: 'hashtag', title: 'Hashtag' },
  {
    name: 'slaTitle',
    title: 'SLA Title',
    getCellValue: (row: SLAPerformance) => {
      return row.slaTitle;
    },
    groupIndex: 0,
  },
  {
    name: 'passed',
    title: 'Passed',
    getCellValue: (row: SLAPerformance) => {
      if (row.slaTitle?.includes('Bookin Accuracy'))
        switch (row.eol) {
          case 'OnTime':
            return 'On Time';
          case 'Late':
            return 'Late';
          case 'Early':
            return 'Early';
          default:
            return '';
        }
      return '';
    },
  },
  { name: 'stream', title: 'Stream' },
  { name: 'slaBreach', visible: false}
];

export const tableColumnExtensions = [
  {
    columnName: 'repairerGroup',
    title: 'Repairer Group',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'repairCode',
    title: 'Repair Code',
    width: 'auto',
    wordWrapEnabled: true,
  },
  {
    columnName: 'regMark',
    title: 'Registration',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'repairerName',
    title: 'Repairer Name',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'claimReference',
    title: 'Claim Reference',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'startDTG',
    title: 'SLA Start',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'expectedDataValue',
    title: 'SLA Expected End',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'actualDataValue',
    title: 'SLA Actual End',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'slaDuration',
    title: 'Duration',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'hashtag',
    title: 'Hashtag',
    wordWrapEnabled: true,
    width: 'auto',
  },
  {
    columnName: 'stream',
    title: 'Stream',
    wordWrapEnabled: true,
    width: 'auto',
  },
];

export const summaryItemsOld = [
  {
    columnName: 'claimReference',
    type: 'count',
  },
  {
    columnName: 'startDTG',
    type: 'percentage',
  },
  {
    columnName: 'slaDuration',
    type: 'average',
    showInGroupFooter: false,
    alignByColumn: true,
  },
];


export const summaryItems = [
  {
    columnName: 'claimReference',
    type: 'count',
    customizeText: ({ value }: { value: string | number | Date }) =>
      `Total Claims: ${value}`,
  },
  { name: 'startDTG', showInColumn: 'startDTG', type: 'custom', customizeText: ({ value }: { value: string | number | Date }) =>
      `SLA Compliance: ${value}`, },
  {
    name: 'slaDurationConverted',
    showInColumn: 'slaDurationConverted',
    type: 'custom',
    customizeText: ({ value }: { value: string | number | Date }) =>
      formatAverageDuration(value),
  },
];

// TODO: move to helpers
export const calculateSelectedRow = (
  options: DataGridTypes.CustomSummaryInfo,
  rows?: any[],
) => {
  switch (options.name) {
    case 'slaDurationConverted': 
      if (options.summaryProcess === 'start') {
        options.totalValue = {
          totalMinutes: 0,
          totalRows: 0,
          timeType: 'S',
        } satisfies DurationSummaryAccumulator;
        break;
      }

      if (options.summaryProcess === 'calculate') {
        if (!isDurationSummaryRow(options.value)) {
          break;
        }

        const accumulator = options.totalValue as DurationSummaryAccumulator;
        const duration = Number(options.value.slaDuration);

        if (!Number.isFinite(duration)) {
          break;
        }

        accumulator.totalMinutes += duration;
        accumulator.totalRows += 1;
        accumulator.timeType = accumulator.totalRows === 1
          ? options.value.timeType || 'S'
          : accumulator.timeType;
        break;
      }



      if (options.summaryProcess === 'finalize') {

        const accumulator = options.totalValue as DurationSummaryAccumulator;

        if (!accumulator || accumulator.totalRows === 0) {
          options.totalValue = '0d 0h 0m';
          break;
        }

        options.totalValue = convertMinToDayHourMin(
          accumulator.totalMinutes / accumulator.totalRows,
          accumulator.timeType || 'S'
        );
      }
      break;
    case 'startDTG':
      if (options.summaryProcess === 'start') {
        options.totalValue = {
          totalRows: 0,
          totalPassed: 0,
        };
        break;
      }

      if (options.summaryProcess === 'calculate') {
        if (!options.value) {
          break;
        }

        const accumulator = options.totalValue as { totalRows: number; totalPassed: number };
        accumulator.totalRows += 1;

        if (options.value.slaBreach === 0) {
          accumulator.totalPassed += 1;
        }

        break;
      }

      if (options.summaryProcess === 'finalize') {
        const accumulator = options.totalValue as { totalRows: number; totalPassed: number };
        let percentageValue = '0.00%';

        if (accumulator && accumulator.totalRows > 0 ) {
          percentageValue = `${Number((accumulator.totalPassed/ accumulator.totalRows ) * 100).toFixed(2)}%`;
        }
        options.totalValue = percentageValue;
      }
      break;
    case 'expectedDataValue':
      if (options.summaryProcess === 'start') {
        options.totalValue = {
          totalRows: 0,
          bookinAccuracyRows: 0,
          onTime: 0,
          late: 0,
          early: 0,
          notSet: 0,
        } satisfies ClaimsSummaryAccumulator;
        break;
      }

      if (options.summaryProcess === 'calculate') {
        if (!isClaimsSummaryRow(options.value)) {
          break;
        }

        const accumulator = options.totalValue as ClaimsSummaryAccumulator;
        accumulator.totalRows += 1;

        if (options.value.slaTitle?.includes('Bookin Accuracy')) {
          accumulator.bookinAccuracyRows += 1;
        }

        switch (options.value.eol) {
          case 'OnTime':
            accumulator.onTime += 1;
            break;
          case 'Late':
            accumulator.late += 1;
            break;
          case 'Early':
            accumulator.early += 1;
            break;
          case 'N/A':
            accumulator.notSet += 1;
            break;
          default:
            break;
        }

        break;
      }

      if (options.summaryProcess === 'finalize') {
        const accumulator = options.totalValue as ClaimsSummaryAccumulator;

        if (!accumulator || accumulator.totalRows === 0) {
          options.totalValue = {
            shouldDisplay: false,
            onTime: '0.0',
            late: '0.0',
            early: '0.0',
            notSet: '0.0',
          };
          break;
        }

        const toPercentage = (value: number) => ((value / accumulator.totalRows) * 100).toFixed(2);
        const shouldDisplay = accumulator.bookinAccuracyRows > 0;

        const expectedDataValueSummary = {
          shouldDisplay: shouldDisplay,
          onTime: toPercentage(accumulator.onTime),
          late: toPercentage(accumulator.late),
          early: toPercentage(accumulator.early),
          notSet: toPercentage(accumulator.notSet),
        }
        options.totalValue = expectedDataValueSummary;
      }
      break;

    default:
      break;
  }
};

export const groupSummaryItemsOld = [
  {
    columnName: 'claimReference',
    type: 'count',
    showInGroupFooter: false,
    alignByColumn: true,

  },
  {
    columnName: 'startDTG',
    type: 'percentage',
    showInGroupFooter: false,
    alignByColumn: true,
  },
  {
    columnName: 'slaDuration',
    type: 'average',
    showInGroupFooter: false,
    alignByColumn: true,

  },
  {
    columnName: 'expectedDataValue',
    type: 'claims_percentage',
    showInGroupFooter: false,
    alignByColumn: true,
  },
];


export const groupSummaryItems = [
  {
    name: 'startDTG',
        showInColumn: 'startDTG',

    type: 'custom',
    showInGroupFooter: false,
    alignByColumn: true,
    customizeText: ({ value }: { value: string | number | Date }) =>
      `SLA Compliance:\n${value}`
  },
  {
    name: 'slaDurationConverted',
    showInColumn: 'slaDurationConverted',
    type: 'custom',
    showInGroupFooter: false,
    alignByColumn: true,
    customizeText: ({value}: {value: string | number | Date}) => formatAverageDuration(value),
  },
  {
    name: 'expectedDataValue',
    showInColumn: 'expectedDataValue',
    type: 'custom',
    showInGroupFooter: false,
    alignByColumn: true,
    customizeText: ({ value }: { value : any }) =>
     value.shouldDisplay ? `${getExpectedDataValueText(value)}` : '', 
  },
];

const getExpectedDataValueText = (value: any) => {
  return `${value.onTime}% On Time\n${value.early}% Early\n${value.late}% Late\n${value.notSet}% Not Set`;
}

export const defaultGroupingColumn = [
  { columnName: 'slaTitle' },
  { columnName: 'repairerGroup' },
  { columnName: 'repairerName' },
];

export const dateColumnName = [
  'startDTG',
  'breachDTG',
  'completedDTG',
  'actualDataValue',
  'expectedDataValue',
];

export const mockedReportData = [
  {
    workProviderName: "Allianz",
    slaTitle: "Notification to First Contact Bookin Accuracy",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "HALO HAL",
    repairerName: "Central HAL Repairer",
    repairCode: "HAL_CEN.5861661390",
    regMark: "REG5861661390",
    claimReference: "MRef5861661390",
    trackingStartDTG: null,
    startDTG: "2025-11-26T05:46:21.83",
    breachDTG: "2025-11-26T09:00:00",
    completedDTG: "2025-11-26T07:25:54",
    slaDuration: -34,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-11-26 09:00:00.000",
    actualDataValue: "2025-11-26 07:25:54.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "Late",
    stream: "ALLIANZ"
  },
  {
    workProviderName: "Allianz",
    slaTitle: "Notification to First Contact Bookin Accuracy",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10880020",
    regMark: "GP47VDA",
    claimReference: "Fcx_31435671_1",
    trackingStartDTG: null,
    startDTG: "2025-05-27T10:49:55",
    breachDTG: "2025-05-27T11:49:55",
    completedDTG: "2026-01-28T11:26:04.647",
    slaDuration: 95076,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-05-27 11:49:55.000",
    actualDataValue: "2026-01-28 11:26:04.647",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "Early",
    stream: "ALLIAN"
  },
  {
    workProviderName: "Allianz",
    slaTitle: "Notification to First Contact Bookin Accuracy",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "MARALL GARAGE",
    repairCode: "MAR_ALL.2134",
    regMark: "MBALLIT1",
    claimReference: "4366",
    trackingStartDTG: null,
    startDTG: "2026-02-06T13:10:24.963",
    breachDTG: "2026-02-06T14:10:24.963",
    completedDTG: "2026-02-19T10:23:02",
    slaDuration: 4693,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-06 14:10:24.963",
    actualDataValue: "2026-02-19 10:23:02.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "OnTime",
    stream: "ALLIANZ"
  },
  {
    workProviderName: "Allianz",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "MARALL GARAGE",
    repairCode: "MAR_ALL.2133",
    regMark: "MONCAR2",
    claimReference: "545453535",
    trackingStartDTG: null,
    startDTG: "2026-02-06T09:59:38.993",
    breachDTG: "2026-02-06T10:59:38.993",
    completedDTG: "2026-02-16T09:02:26",
    slaDuration: 3183,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-06 10:59:38.993",
    actualDataValue: "2026-02-16 09:02:26.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "Late",
    stream: "ALLIANZ"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25930031",
    regMark: "DE11PBZ",
    claimReference: "702786633",
    trackingStartDTG: null,
    startDTG: "2025-08-04T05:04:11.32",
    breachDTG: "2025-08-12T05:04:11.32",
    completedDTG: "2026-01-15T10:47:00",
    slaDuration: 236502,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-08-12 05:04:11.320",
    actualDataValue: "2026-01-15 10:47:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "Late",
    stream: "CHURCH    "
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960004",
    regMark: "DE11PBZ",
    claimReference: "702786633",
    trackingStartDTG: null,
    startDTG: "2026-01-13T12:12:00",
    breachDTG: "2026-01-21T12:12:00",
    completedDTG: "2026-01-13T17:30:00",
    slaDuration: 318,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-21 12:12:00.000",
    actualDataValue: "2026-01-13 17:30:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "Early",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25950001",
    regMark: "FE15KZZ",
    claimReference: "702454224/1",
    trackingStartDTG: null,
    startDTG: "2025-10-22T05:20:02.92",
    breachDTG: "2025-10-30T05:20:02.92",
    completedDTG: "2025-12-29T13:59:00",
    slaDuration: 98438,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-10-30 05:20:02.920",
    actualDataValue: "2025-12-29 13:59:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "PRIV      "
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25950002",
    regMark: "TEST3",
    claimReference: "Unknown",
    trackingStartDTG: null,
    startDTG: "2025-12-23T13:00:00",
    breachDTG: "2025-12-31T13:00:00",
    completedDTG: "2025-12-30T09:09:00",
    slaDuration: 9849,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-12-31 13:00:00.000",
    actualDataValue: "2025-12-30 09:09:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960002",
    regMark: "TEST3",
    claimReference: "4567",
    trackingStartDTG: null,
    startDTG: "2026-01-07T12:12:00",
    breachDTG: "2026-01-15T12:12:00",
    completedDTG: "2026-01-07T16:18:00",
    slaDuration: 246,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-15 12:12:00.000",
    actualDataValue: "2026-01-07 16:18:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960001",
    regMark: "TEST3",
    claimReference: "1234",
    trackingStartDTG: null,
    startDTG: "2026-01-07T11:11:00",
    breachDTG: "2026-01-15T11:11:00",
    completedDTG: "2026-01-07T16:08:00",
    slaDuration: 297,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-15 11:11:00.000",
    actualDataValue: "2026-01-07 16:08:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960006",
    regMark: "BJ57TWM",
    claimReference: "702867003",
    trackingStartDTG: null,
    startDTG: "2026-01-23T09:00:00",
    breachDTG: "2026-01-31T09:00:00",
    completedDTG: "2026-01-23T10:28:00",
    slaDuration: 88,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-31 09:00:00.000",
    actualDataValue: "2026-01-23 10:28:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960007",
    regMark: "S5",
    claimReference: "702867003",
    trackingStartDTG: null,
    startDTG: "2026-01-23T09:00:00",
    breachDTG: "2026-01-31T09:00:00",
    completedDTG: "2026-01-23T10:37:00",
    slaDuration: 97,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-31 09:00:00.000",
    actualDataValue: "2026-01-23 10:37:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960005",
    regMark: "LS56RFN",
    claimReference: "702786674",
    trackingStartDTG: null,
    startDTG: "2026-01-13T12:12:00",
    breachDTG: "2026-01-21T12:12:00",
    completedDTG: "2026-01-13T18:24:00",
    slaDuration: 372,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-21 12:12:00.000",
    actualDataValue: "2026-01-13 18:24:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960003",
    regMark: "BN16JZT",
    claimReference: "702776527",
    trackingStartDTG: null,
    startDTG: "2026-01-13T16:27:00",
    breachDTG: "2026-01-21T16:27:00",
    completedDTG: "2026-01-13T16:49:00",
    slaDuration: 22,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-21 16:27:00.000",
    actualDataValue: "2026-01-13 16:49:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.3142948",
    regMark: "AD16CDE",
    claimReference: "CLM-2026-03022606",
    trackingStartDTG: null,
    startDTG: "2026-02-07T11:05:24",
    breachDTG: "2026-02-08T11:05:24",
    completedDTG: "2026-02-09T11:05:24",
    slaDuration: 2880,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-08 11:05:24.000",
    actualDataValue: "2026-02-09 11:05:24.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960003",
    regMark: "WN18YEU",
    claimReference: "702778044",
    trackingStartDTG: null,
    startDTG: "2026-01-09T13:00:00",
    breachDTG: "2026-01-17T13:00:00",
    completedDTG: "2026-01-09T15:41:00",
    slaDuration: 161,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-17 13:00:00.000",
    actualDataValue: "2026-01-09 15:41:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960004",
    regMark: "T5",
    claimReference: "702776352",
    trackingStartDTG: null,
    startDTG: "2026-01-09T13:00:00",
    breachDTG: "2026-01-17T13:00:00",
    completedDTG: "2026-01-09T15:48:00",
    slaDuration: 168,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-17 13:00:00.000",
    actualDataValue: "2026-01-09 15:48:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960002",
    regMark: "S5",
    claimReference: "702776600",
    trackingStartDTG: null,
    startDTG: "2026-01-09T13:00:00",
    breachDTG: "2026-01-17T13:00:00",
    completedDTG: "2026-01-09T15:32:00",
    slaDuration: 152,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-17 13:00:00.000",
    actualDataValue: "2026-01-09 15:32:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to OnSite",
    slaDescription: "Initial notification to vehicle being marked on site (variable SLA for driveable/non-driveable)",
    timeType: "S",
    repairerGroup: "PINETREE",
    repairerName: "Pinetree Repairer1",
    repairCode: "EVAL_DEV1.2213",
    regMark: "TESTAT34",
    claimReference: "",
    trackingStartDTG: null,
    startDTG: "2026-02-27T15:38:15",
    breachDTG: "2026-02-28T15:38:15",
    completedDTG: "2026-02-27T15:38:35",
    slaDuration: 0,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-28 15:38:15.000",
    actualDataValue: "2026-02-27 15:38:35.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DLGDLINE"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to Delivered",
    slaDescription: "Initial notification through to the vehicle being returned",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960003",
    regMark: "BN16JZT",
    claimReference: "702776527",
    trackingStartDTG: null,
    startDTG: "2026-01-13T16:27:00",
    breachDTG: "2026-02-03T16:27:00",
    completedDTG: "2026-01-13T16:50:00",
    slaDuration: 23,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-03 16:27:00.000",
    actualDataValue: "2026-01-13 16:50:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to Delivered",
    slaDescription: "Initial notification through to the vehicle being returned",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960004",
    regMark: "DE11PBZ",
    claimReference: "702786633",
    trackingStartDTG: null,
    startDTG: "2026-01-13T12:12:00",
    breachDTG: "2026-02-03T12:12:00",
    completedDTG: "2026-01-13T17:37:00",
    slaDuration: 325,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-03 12:12:00.000",
    actualDataValue: "2026-01-13 17:37:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to Delivered",
    slaDescription: "Initial notification through to the vehicle being returned",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960005",
    regMark: "LS56RFN",
    claimReference: "702786674",
    trackingStartDTG: null,
    startDTG: "2026-01-13T12:12:00",
    breachDTG: "2026-02-03T12:12:00",
    completedDTG: "2026-01-13T18:31:00",
    slaDuration: 379,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-03 12:12:00.000",
    actualDataValue: "2026-01-13 18:31:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to Delivered",
    slaDescription: "Initial notification through to the vehicle being returned",
    timeType: "S",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960001",
    regMark: "TEST3",
    claimReference: "1234",
    trackingStartDTG: null,
    startDTG: "2026-01-07T11:11:00",
    breachDTG: "2026-01-28T11:11:00",
    completedDTG: "2026-01-07T16:11:00",
    slaDuration: 300,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-28 11:11:00.000",
    actualDataValue: "2026-01-07 16:11:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to Delivered",
    slaDescription: "Initial notification through to the vehicle being returned",
    timeType: "S",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.3142948",
    regMark: "AD16CDE",
    claimReference: "CLM-2026-03022606",
    trackingStartDTG: null,
    startDTG: "2026-02-07T11:05:24",
    breachDTG: "2026-02-28T11:05:24",
    completedDTG: "2026-02-12T12:05:24",
    slaDuration: 7260,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-28 11:05:24.000",
    actualDataValue: "2026-02-12 12:05:24.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960001",
    regMark: "TEST3",
    claimReference: "1234",
    trackingStartDTG: null,
    startDTG: "2026-01-07T11:11:00",
    breachDTG: "2026-01-07T14:11:00",
    completedDTG: "2026-01-07T16:11:42",
    slaDuration: 301,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-07 14:11:00.000",
    actualDataValue: "2026-01-07 16:11:42.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25950002",
    regMark: "TEST3",
    claimReference: "Unknown",
    trackingStartDTG: null,
    startDTG: "2025-12-23T13:00:00",
    breachDTG: "2025-12-23T16:00:00",
    completedDTG: "2026-01-28T11:26:06.217",
    slaDuration: 13946,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-12-23 16:00:00.000",
    actualDataValue: "2026-01-28 11:26:06.217",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25950001",
    regMark: "FE15KZZ",
    claimReference: "702454224/1",
    trackingStartDTG: null,
    startDTG: "2025-10-22T05:20:02.92",
    breachDTG: "2025-10-22T11:00:00",
    completedDTG: "2026-01-28T11:26:05.263",
    slaDuration: 38006,
    count: 1,
    slaType: 0,
    expectedDataValue: "2025-10-22 11:00:00.000",
    actualDataValue: "2026-01-28 11:26:05.263",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "PRIV      "
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960002",
    regMark: "TEST3",
    claimReference: "4567",
    trackingStartDTG: null,
    startDTG: "2026-01-07T12:12:00",
    breachDTG: "2026-01-07T15:12:00",
    completedDTG: "2026-01-07T16:19:21",
    slaDuration: 247,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-07 15:12:00.000",
    actualDataValue: "2026-01-07 16:19:21.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960007",
    regMark: "S5",
    claimReference: "702867003",
    trackingStartDTG: null,
    startDTG: "2026-01-23T09:00:00",
    breachDTG: "2026-01-23T12:00:00",
    completedDTG: "2026-01-28T11:26:18.243",
    slaDuration: 1766,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-23 12:00:00.000",
    actualDataValue: "2026-01-28 11:26:18.243",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960005",
    regMark: "LS56RFN",
    claimReference: "702786674",
    trackingStartDTG: null,
    startDTG: "2026-01-13T12:12:00",
    breachDTG: "2026-01-13T15:12:00",
    completedDTG: "2026-01-28T11:26:15.2",
    slaDuration: 5894,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-13 15:12:00.000",
    actualDataValue: "2026-01-28 11:26:15.200",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960006",
    regMark: "BJ57TWM",
    claimReference: "702867003",
    trackingStartDTG: null,
    startDTG: "2026-01-23T09:00:00",
    breachDTG: "2026-01-23T12:00:00",
    completedDTG: "2026-01-28T11:26:17.6",
    slaDuration: 1766,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-23 12:00:00.000",
    actualDataValue: "2026-01-28 11:26:17.600",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960004",
    regMark: "DE11PBZ",
    claimReference: "702786633",
    trackingStartDTG: null,
    startDTG: "2026-01-13T12:12:00",
    breachDTG: "2026-01-13T15:12:00",
    completedDTG: "2026-01-28T11:26:14.657",
    slaDuration: 5894,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-13 15:12:00.000",
    actualDataValue: "2026-01-28 11:26:14.657",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960003",
    regMark: "BN16JZT",
    claimReference: "702776527",
    trackingStartDTG: null,
    startDTG: "2026-01-13T16:27:00",
    breachDTG: "2026-01-14T10:27:00",
    completedDTG: "2026-01-28T11:26:13.403",
    slaDuration: 5639,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-14 10:27:00.000",
    actualDataValue: "2026-01-28 11:26:13.403",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.3142948",
    regMark: "AD16CDE",
    claimReference: "CLM-2026-03022606",
    trackingStartDTG: null,
    startDTG: "2026-02-07T11:05:24",
    breachDTG: "2026-02-09T11:00:00",
    completedDTG: "2026-02-07T11:05:24",
    slaDuration: 0,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-09 11:00:00.000",
    actualDataValue: "2026-02-07 11:05:24.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960002",
    regMark: "S5",
    claimReference: "702776600",
    trackingStartDTG: null,
    startDTG: "2026-01-09T13:00:00",
    breachDTG: "2026-01-09T16:00:00",
    completedDTG: "2026-01-28T11:26:10.073",
    slaDuration: 6926,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-09 16:00:00.000",
    actualDataValue: "2026-01-28 11:26:10.073",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960003",
    regMark: "WN18YEU",
    claimReference: "702778044",
    trackingStartDTG: null,
    startDTG: "2026-01-09T13:00:00",
    breachDTG: "2026-01-09T16:00:00",
    completedDTG: "2026-01-28T11:26:10.577",
    slaDuration: 6926,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-09 16:00:00.000",
    actualDataValue: "2026-01-28 11:26:10.577",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960004",
    regMark: "T5",
    claimReference: "702776352",
    trackingStartDTG: null,
    startDTG: "2026-01-09T13:00:00",
    breachDTG: "2026-01-09T16:00:00",
    completedDTG: "2026-01-28T11:26:11.147",
    slaDuration: 6926,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-09 16:00:00.000",
    actualDataValue: "2026-01-28 11:26:11.147",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "PINETREE",
    repairerName: "Pinetree Repairer1",
    repairCode: "EVAL_DEV1.2213",
    regMark: "TESTAT34",
    claimReference: "",
    trackingStartDTG: null,
    startDTG: "2026-02-27T15:38:15",
    breachDTG: "2026-03-02T09:39:00",
    completedDTG: "2026-02-27T15:38:40",
    slaDuration: 0,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-03-02 09:39:00.000",
    actualDataValue: "2026-02-27 15:38:40.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DLGDLINE"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "Notification to First Contact",
    slaDescription: "Initial notification through to the first recorded customer contact",
    timeType: "W",
    repairerGroup: "PINETREE",
    repairerName: "Pinetree Repairer1",
    repairCode: "EVAL_DEV1.2215",
    regMark: "ABCD11",
    claimReference: "1234a",
    trackingStartDTG: null,
    startDTG: "2026-02-27T16:04:12.733",
    breachDTG: "2026-03-02T10:05:00",
    completedDTG: "2026-02-27T16:05:18",
    slaDuration: 1,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-03-02 10:05:00.000",
    actualDataValue: "2026-02-27 16:05:18.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DLGCHURCH"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960002",
    regMark: "TEST3",
    claimReference: "4567",
    trackingStartDTG: null,
    startDTG: "2026-01-07T16:18:00",
    breachDTG: "2026-01-14T16:18:00",
    completedDTG: "2026-01-07T16:24:13",
    slaDuration: 6,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-14 16:18:00.000",
    actualDataValue: "2026-01-07 16:24:13.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960004",
    regMark: "DE11PBZ",
    claimReference: "702786633",
    trackingStartDTG: null,
    startDTG: "2026-01-13T17:30:00",
    breachDTG: "2026-01-20T17:30:00",
    completedDTG: "2026-01-13T17:37:20",
    slaDuration: 7,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-20 17:30:00.000",
    actualDataValue: "2026-01-13 17:37:20.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960007",
    regMark: "S5",
    claimReference: "702867003",
    trackingStartDTG: null,
    startDTG: "2026-01-23T10:37:00",
    breachDTG: "2026-01-30T10:37:00",
    completedDTG: "2026-01-23T10:43:53",
    slaDuration: 6,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-30 10:37:00.000",
    actualDataValue: "2026-01-23 10:43:53.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960006",
    regMark: "BJ57TWM",
    claimReference: "702867003",
    trackingStartDTG: null,
    startDTG: "2026-01-23T10:28:00",
    breachDTG: "2026-01-30T10:28:00",
    completedDTG: "2026-01-23T10:34:14",
    slaDuration: 6,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-30 10:28:00.000",
    actualDataValue: "2026-01-23 10:34:14.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960005",
    regMark: "LS56RFN",
    claimReference: "702786674",
    trackingStartDTG: null,
    startDTG: "2026-01-13T18:24:00",
    breachDTG: "2026-01-20T18:24:00",
    completedDTG: "2026-01-13T18:31:37",
    slaDuration: 7,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-20 18:24:00.000",
    actualDataValue: "2026-01-13 18:31:37.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960003",
    regMark: "BN16JZT",
    claimReference: "702776527",
    trackingStartDTG: null,
    startDTG: "2026-01-13T16:49:00",
    breachDTG: "2026-01-20T16:49:00",
    completedDTG: "2026-01-13T16:50:33",
    slaDuration: 1,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-20 16:49:00.000",
    actualDataValue: "2026-01-13 16:50:33.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25950001",
    regMark: "FE15KZZ",
    claimReference: "702454224/1",
    trackingStartDTG: null,
    startDTG: "2025-12-29T13:59:00",
    breachDTG: "2026-01-05T13:59:00",
    completedDTG: "2025-12-29T14:16:10",
    slaDuration: 17,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-05 13:59:00.000",
    actualDataValue: "2025-12-29 14:16:10.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "PRIV      "
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25930031",
    regMark: "DE11PBZ",
    claimReference: "702786633",
    trackingStartDTG: null,
    startDTG: "2026-01-15T10:47:00",
    breachDTG: "2026-01-22T10:47:00",
    completedDTG: "2026-01-15T10:52:06",
    slaDuration: 5,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-22 10:47:00.000",
    actualDataValue: "2026-01-15 10:52:06.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "CHURCH    "
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25950002",
    regMark: "TEST3",
    claimReference: "Unknown",
    trackingStartDTG: null,
    startDTG: "2025-12-30T09:09:00",
    breachDTG: "2026-01-06T09:09:00",
    completedDTG: "2025-12-30T09:19:44",
    slaDuration: 10,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-06 09:09:00.000",
    actualDataValue: "2025-12-30 09:19:44.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "DLGAS",
    repairerName: "DLG Auto Services (Bristol)",
    repairCode: "DLG_BRI.25960001",
    regMark: "TEST3",
    claimReference: "1234",
    trackingStartDTG: null,
    startDTG: "2026-01-07T16:08:00",
    breachDTG: "2026-01-14T16:08:00",
    completedDTG: "2026-01-07T16:09:54",
    slaDuration: 1,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-14 16:08:00.000",
    actualDataValue: "2026-01-07 16:09:54.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group ",
    slaTitle: "OnSite to Completed Bookin Accuracy",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.3142948",
    regMark: "AD16CDE",
    claimReference: "CLM-2026-03022606",
    trackingStartDTG: null,
    startDTG: "2026-02-09T11:05:24",
    breachDTG: "2026-02-16T11:05:24",
    completedDTG: "2026-02-12T11:30:24",
    slaDuration: 4345,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-16 11:05:24.000",
    actualDataValue: "2026-02-12 11:30:24.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "DIRECT"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960002",
    regMark: "S5",
    claimReference: "702776600",
    trackingStartDTG: null,
    startDTG: "2026-01-09T15:32:00",
    breachDTG: "2026-01-16T15:32:00",
    completedDTG: "2026-01-09T15:39:00",
    slaDuration: 7,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-16 15:32:00.000",
    actualDataValue: "2026-01-09 15:39:00.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960003",
    regMark: "WN18YEU",
    claimReference: "702778044",
    trackingStartDTG: null,
    startDTG: "2026-01-09T15:41:00",
    breachDTG: "2026-01-16T15:41:00",
    completedDTG: "2026-01-09T15:45:57",
    slaDuration: 4,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-16 15:41:00.000",
    actualDataValue: "2026-01-09 15:45:57.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Direct Line Group",
    slaTitle: "OnSite to Completed",
    slaDescription: "Vehicle OnSite to completion of repairs",
    timeType: "EBH",
    repairerGroup: "INDEPENDENT",
    repairerName: "BMS DXTEST TEST DLG A (REST)",
    repairCode: "BMTSA.10960004",
    regMark: "T5",
    claimReference: "702776352",
    trackingStartDTG: null,
    startDTG: "2026-01-09T15:48:00",
    breachDTG: "2026-01-16T15:48:00",
    completedDTG: "2026-01-09T15:53:54",
    slaDuration: 5,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-01-16 15:48:00.000",
    actualDataValue: "2026-01-09 15:53:54.000",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 0,
    hashtag: "#CHOICE",
    eol: "N/A",
    stream: "UKINSTP"
  },
  {
    workProviderName: "Allianz",
    slaTitle: "Notification to Bookin set or Onsite Bookin Accuracy",
    slaDescription: "Notification to book in arranged or vehicle on site",
    timeType: "W",
    repairerGroup: "INDEPENDENT",
    repairerName: "MARALL GARAGE",
    repairCode: "MAR_ALL.2133",
    regMark: "MONCAR2",
    claimReference: "545453535",
    trackingStartDTG: null,
    startDTG: "2026-02-06T09:59:38.993",
    breachDTG: "2026-02-11T17:00:00",
    completedDTG: "2026-02-16T09:04:36.953",
    slaDuration: 3185,
    count: 1,
    slaType: 0,
    expectedDataValue: "2026-02-11 17:00:00.000",
    actualDataValue: "2026-02-16 09:04:36.953",
    dataSuccess: null,
    slaCompleted: true,
    slaWarning: 1,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "Late",
    stream: "ALLIANZ"
  },
  {
    workProviderName: "Allianz",
    slaTitle: "Bookin Accuracy (Allianz)",
    slaDescription: "Does the actual onsite date match the final book in date set within the agreed period",
    timeType: "EBH",
    repairerGroup: "INDEPENDENT",
    repairerName: "MARALL GARAGE",
    repairCode: "MAR_ALL.2133",
    regMark: "MONCAR2",
    claimReference: "545453535",
    trackingStartDTG: null,
    startDTG: "2026-02-16T00:00:00",
    breachDTG: "2026-02-16T00:00:00",
    completedDTG: "2026-02-16T00:00:00",
    slaDuration: null,
    count: 1,
    slaType: 1,
    expectedDataValue: "2026-02-20",
    actualDataValue: "2026-02-16",
    dataSuccess: 0,
    slaCompleted: true,
    slaWarning: 0,
    slaBreach: 1,
    hashtag: "#CHOICE",
    eol: "Early",
    stream: "ALLIANZ"
  }
]
